import { useState, useMemo } from "react";
import { graphql, useMutation } from "react-relay";
import { useLoaderData, useRevalidator } from "react-router";

import {
    Button,
    Card,
    Flex,
    Input,
    Label,
    TextField,
    View,
    Text,
} from "@phoenix/components";
import { useNotifySuccess } from "@phoenix/contexts";

// Full list of Presidio-supported PII entity types
const PII_ENTITIES = [
    // General
    { label: "Person / Name", value: "PERSON", group: "General" },
    { label: "Email Address", value: "EMAIL_ADDRESS", group: "General" },
    { label: "Phone Number", value: "PHONE_NUMBER", group: "General" },
    { label: "Location / Address", value: "LOCATION", group: "General" },
    { label: "Date / Time", value: "DATE_TIME", group: "General" },
    { label: "IP Address", value: "IP_ADDRESS", group: "General" },
    { label: "URL", value: "URL", group: "General" },
    { label: "Credit Card Number", value: "CREDIT_CARD", group: "General" },
    { label: "Crypto Wallet Address", value: "CRYPTO", group: "General" },
    { label: "IBAN Code", value: "IBAN_CODE", group: "General" },
    { label: "NRP (Nationality / Religion / Politics)", value: "NRP", group: "General" },
    { label: "Medical License", value: "MEDICAL_LICENSE", group: "General" },
    // United States
    { label: "US SSN", value: "US_SSN", group: "United States" },
    { label: "US Passport", value: "US_PASSPORT", group: "United States" },
    { label: "US Bank Number", value: "US_BANK_NUMBER", group: "United States" },
    { label: "US Driver License", value: "US_DRIVER_LICENSE", group: "United States" },
    { label: "US ITIN", value: "US_ITIN", group: "United States" },
    // United Kingdom
    { label: "UK NHS Number", value: "UK_NHS", group: "United Kingdom" },
    // India
    { label: "IN PAN Card", value: "IN_PAN", group: "India" },
    { label: "IN Aadhaar Number", value: "IN_AADHAAR", group: "India" },
    { label: "IN Vehicle Registration", value: "IN_VEHICLE_REGISTRATION", group: "India" },
    { label: "IN Voter ID", value: "IN_VOTER", group: "India" },
    // Australia
    { label: "AU ABN", value: "AU_ABN", group: "Australia" },
    { label: "AU ACN", value: "AU_ACN", group: "Australia" },
    { label: "AU TFN", value: "AU_TFN", group: "Australia" },
    { label: "AU Medicare Number", value: "AU_MEDICARE", group: "Australia" },
    // Singapore
    { label: "SG NRIC / FIN", value: "SG_NRIC_FIN", group: "Singapore" },
    // Italy
    { label: "IT Fiscal Code", value: "IT_FISCAL_CODE", group: "Italy" },
    { label: "IT Driver License", value: "IT_DRIVER_LICENSE", group: "Italy" },
    { label: "IT VAT Code", value: "IT_VAT_CODE", group: "Italy" },
    { label: "IT Passport", value: "IT_PASSPORT", group: "Italy" },
    { label: "IT Identity Card", value: "IT_IDENTITY_CARD", group: "Italy" },
    // Poland
    { label: "PL PESEL", value: "PL_PESEL", group: "Poland" },
    // Spain
    { label: "ES NIF", value: "ES_NIF", group: "Spain" },
    // Finland
    { label: "FI Personal Identity Code", value: "FI_PERSONAL_IDENTITY_CODE", group: "Finland" },
];

export const SettingsAlertsPage = () => {

    const data = useLoaderData() as any;
    const { revalidate } = useRevalidator();
    const notifySuccess = useNotifySuccess();

    // SMTP Settings State
    const [host, setHost] = useState(data?.smtpConfig?.host ?? "");
    const [port, setPort] = useState(data?.smtpConfig?.port?.toString() ?? "587");
    const [username, setUsername] = useState(data?.smtpConfig?.username ?? "");
    const [password, setPassword] = useState("");
    const [senderAddress, setSenderAddress] = useState(
        data?.smtpConfig?.senderAddress ?? ""
    );
    const [recipientAddress, setRecipientAddress] = useState(
        data?.smtpConfig?.recipientAddress ?? ""
    );

    // Alert Preferences State
    const [piiEnabled, setPiiEnabled] = useState(
        data?.alertPreferences?.piiEnabled ?? true
    );
    const [costAlertEnabled, setCostAlertEnabled] = useState(
        data?.alertPreferences?.costAlertEnabled ?? false
    );
    const [costThreshold, setCostThreshold] = useState(
        data?.alertPreferences?.costThreshold?.toString() ?? "0.0"
    );
    const [errorAlertEnabled, setErrorAlertEnabled] = useState(
        data?.alertPreferences?.errorAlertEnabled ?? false
    );
    const [systemHealthAlertEnabled, setSystemHealthAlertEnabled] = useState(
        data?.alertPreferences?.systemHealthAlertEnabled ?? true
    );

    // PII Masking State — "maskedEntities" are the ones that WILL be masked.
    // Backend stores "excludedEntities" (inverse). We compute the complement on load/save.
    const ALL_VALUES = PII_ENTITIES.map((e) => e.value);
    const dbExcluded: string[] = data?.piiConfig?.excludedEntities ?? ["DATE_TIME", "LOCATION"];
    const [maskedEntities, setMaskedEntities] = useState<string[]>(
        ALL_VALUES.filter((v) => !dbExcluded.includes(v))
    );

    const handleToggleEntity = (entityValue: string) => {
        setMaskedEntities((prev) => {
            if (prev.includes(entityValue)) {
                return prev.filter((item) => item !== entityValue);
            } else {
                return [...prev, entityValue];
            }
        });
    };

    // PII search/filter state
    const [entitySearch, setEntitySearch] = useState("");

    // Filtered + grouped entities based on search input
    const filteredGroups = useMemo(() => {
        const q = entitySearch.trim().toLowerCase();
        const groups: string[] = [];
        const map: Record<string, typeof PII_ENTITIES> = {};
        for (const entity of PII_ENTITIES) {
            if (
                !q ||
                entity.label.toLowerCase().includes(q) ||
                entity.value.toLowerCase().includes(q) ||
                entity.group.toLowerCase().includes(q)
            ) {
                if (!map[entity.group]) {
                    map[entity.group] = [];
                    groups.push(entity.group);
                }
                map[entity.group].push(entity);
            }
        }
        return groups.map((group) => ({ group, entities: map[group] }));
    }, [entitySearch]);

    const [updateSmtpConfig, isSmtpPending] = useMutation(graphql`
    mutation SettingsAlertsPageUpdateSmtpMutation($input: SMTPConfigInput!) {
      updateSmtpConfig(input: $input) {
        success
      }
    }
  `);

    const [updateAlertPreferences, isAlertPending] = useMutation(graphql`
    mutation SettingsAlertsPageUpdateAlertsMutation($input: UpdateAlertPreferencesInput!) {
      updateAlertPreferences(input: $input) {
        success
      }
    }
  `);

    const [updatePiiConfig, isPiiPending] = useMutation(graphql`
    mutation SettingsAlertsPageUpdatePiiMutation($input: PIIConfigInput!) {
      updatePiiConfig(input: $input) {
        success
      }
    }
  `);

    const [testSmtpConfig, isTestPending] = useMutation(graphql`
    mutation SettingsAlertsPageTestSmtpMutation($input: SMTPConfigInput!) {
      testSmtpConfig(input: $input) {
        success
      }
    }
  `);


    const handleTestSmtp = () => {
        testSmtpConfig({
            variables: {
                input: {
                    host,
                    port: parseInt(port, 10),
                    username,
                    password,
                    senderAddress,
                    recipientAddress,
                },
            },
            onCompleted: (response: any) => {
                if (response.testSmtpConfig.success) {
                    notifySuccess({
                        title: "Test Successful",
                        message: "A test email has been sent successfully.",
                    });
                } else {
                    // Assuming we have a notifyError, but for now just console/alert or use notifySuccess with warning style if possible
                    // Reusing notifySuccess for simplicity unless I find notifyError
                    notifySuccess({
                        title: "Test Failed",
                        message: "Failed to send test email. Please check your SMTP settings and logs.",
                    });
                }
            },
        });
    };

    const handleSave = () => {
        // Save SMTP Config
        updateSmtpConfig({
            variables: {
                input: {
                    host,
                    port: parseInt(port, 10),
                    username,
                    password,
                    senderAddress,
                    recipientAddress,
                },
            },
            onCompleted: () => {
                // Save Alert Preferences
                updateAlertPreferences({
                    variables: {
                        input: {
                            piiEnabled,
                            costAlertEnabled,
                            costThreshold: parseFloat(costThreshold),
                            errorAlertEnabled,
                            systemHealthAlertEnabled,
                        },
                    },
                    onCompleted: () => {
                        // Save PII Config Exclusions
                        updatePiiConfig({
                            variables: {
                                input: {
                                    // Send the complement: everything NOT selected for masking
                                    excludedEntities: ALL_VALUES.filter((v) => !maskedEntities.includes(v)),
                                },
                            },
                            onCompleted: () => {
                                notifySuccess({
                                    title: "Settings Saved",
                                    message: "Alert configuration and preferences updated successfully.",
                                });
                                revalidate();
                            },
                        });
                    },
                });
            },
        });
    };


    return (
        <Flex direction="column" gap="size-200" width="100%">
            <Card title="SMTP Configuration">
                <View padding="size-200">
                    <Flex direction="column" gap="size-300">
                        <Flex direction="row" gap="size-200" wrap="wrap">
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={host} onChange={setHost}>
                                    <Label>SMTP Host</Label>
                                    <Input placeholder="smtp.gmail.com" />
                                </TextField>
                            </View>
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={port} onChange={setPort}>
                                    <Label>SMTP Port</Label>
                                    <Input placeholder="587" type="number" />
                                </TextField>
                            </View>
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={username} onChange={setUsername}>
                                    <Label>Username</Label>
                                    <Input placeholder="your-email@gmail.com" />
                                </TextField>
                            </View>
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={password} onChange={setPassword}>
                                    <Label>Password / API Key</Label>
                                    <Input type="password" placeholder="••••••••" />
                                </TextField>
                            </View>
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={senderAddress} onChange={setSenderAddress}>
                                    <Label>Sender Email Address</Label>
                                    <Input placeholder="no-reply@yourdomain.com" />
                                </TextField>
                            </View>
                            <View flexBasis="calc(33.333% - 14px)">
                                <TextField value={recipientAddress} onChange={setRecipientAddress}>
                                    <Label>Alert Recipient Email</Label>
                                    <Input placeholder="security-alerts@yourdomain.com" />
                                </TextField>
                            </View>
                        </Flex>
                        <View>
                            <Button
                                variant="default"
                                onPress={handleTestSmtp}
                                isDisabled={isTestPending || !host || !username || !recipientAddress}
                            >
                                Test SMTP Connection
                            </Button>
                        </View>
                    </Flex>
                </View>
            </Card>

            <Card title="PII Masking">
                <View padding="size-200">
                    <Flex direction="column" gap="size-200">
                        <Text style={{ color: "var(--global-color-gray-700)" }}>
                            Select PII entity categories to <strong>mask</strong>. Only checked types will be detected and masked in spans and traces.
                        </Text>

                        {/* Search input */}
                        <div style={{ position: "relative" }}>
                            <input
                                type="text"
                                placeholder="🔍  Search by name, code, or country (e.g. email, SSN, India, Australia…)"
                                value={entitySearch}
                                onChange={(e) => setEntitySearch(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "1px solid var(--global-color-gray-300)",
                                    borderRadius: 6,
                                    fontSize: 13,
                                    boxSizing: "border-box",
                                    outline: "none",
                                    background: "var(--spectrum-alias-background-color-default)",
                                    color: "inherit",
                                }}
                            />
                        </div>

                        {/* Select all / Clear all + counter */}
                        <Flex direction="row" gap="size-100" alignItems="center">
                            <button
                                onClick={() => setMaskedEntities(ALL_VALUES)}
                                style={{
                                    background: "transparent",
                                    border: "1px solid var(--global-color-gray-400)",
                                    borderRadius: 4,
                                    padding: "3px 12px",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    color: "var(--global-color-gray-700)",
                                }}
                            >
                                Select all
                            </button>
                            <button
                                onClick={() => setMaskedEntities([])}
                                style={{
                                    background: "transparent",
                                    border: "1px solid var(--global-color-gray-400)",
                                    borderRadius: 4,
                                    padding: "3px 12px",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    color: "var(--global-color-gray-700)",
                                }}
                            >
                                Clear all
                            </button>
                            <Text style={{ fontSize: 12, color: "var(--global-color-gray-500)" }}>
                                {maskedEntities.length} of {PII_ENTITIES.length} selected
                            </Text>
                        </Flex>

                        {/* Scrollable entity list — 2-column grid per group */}
                        <div
                            style={{
                                maxHeight: 320,
                                overflowY: "auto",
                                border: "1px solid var(--global-color-gray-200)",
                                borderRadius: 6,
                                padding: "8px 0",
                            }}
                        >
                            {filteredGroups.length === 0 ? (
                                <Text style={{ padding: "12px 16px", color: "var(--global-color-gray-600)" }}>
                                    No entities match your search.
                                </Text>
                            ) : (
                                filteredGroups.map(({ group, entities }) => (
                                    <div key={group}>
                                        {/* Group header */}
                                        <div
                                            style={{
                                                padding: "5px 16px 3px",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.06em",
                                                color: "var(--global-color-gray-700)",
                                                background: "var(--global-color-gray-100)",
                                                borderBottom: "1px solid var(--global-color-gray-200)",
                                            }}
                                        >
                                            {group}
                                        </div>
                                        {/* 2-column grid for entity checkboxes */}
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: 0,
                                            }}
                                        >
                                            {entities.map((entity) => {
                                                const isChecked = maskedEntities.includes(entity.value);
                                                return (
                                                    <label
                                                        key={entity.value}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 9,
                                                            padding: "7px 16px",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleToggleEntity(entity.value)}
                                                            style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#2680eb", flexShrink: 0 }}
                                                        />
                                                        <span style={{ fontSize: 13 }}>{entity.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                    </Flex>
                </View>
            </Card>

            <Card title="Alert Preferences">
                <View padding="size-200">
                    <Flex direction="column" gap="size-300">
                        <Flex direction="row" gap="size-150" alignItems="center">
                            <input
                                type="checkbox"
                                checked={piiEnabled}
                                onChange={(e) => setPiiEnabled(e.target.checked)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                            <Label>Enable PII Detection Alerts</Label>
                        </Flex>

                        <Flex direction="column" gap="size-100">
                            <Flex direction="row" gap="size-150" alignItems="center">
                                <input
                                    type="checkbox"
                                    checked={costAlertEnabled}
                                    onChange={(e) => setCostAlertEnabled(e.target.checked)}
                                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                                />
                                <Label>Enable Budget / Cost Alerts</Label>
                            </Flex>
                            {costAlertEnabled && (
                                <div style={{ marginLeft: 32, marginTop: 4 }}>
                                    <TextField
                                        value={costThreshold}
                                        onChange={setCostThreshold}
                                    >
                                        <Label>Cost Threshold ($)</Label>
                                        <Input type="number" step="0.01" placeholder="15.00" />
                                        <Text slot="description">Trigger mail when total project cost reached this value.</Text>
                                    </TextField>
                                </div>
                            )}
                        </Flex>

                        <Flex direction="row" gap="size-150" alignItems="center">
                            <input
                                type="checkbox"
                                checked={errorAlertEnabled}
                                onChange={(e) => setErrorAlertEnabled(e.target.checked)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                            <Label>Enable Error Rate Alerts</Label>
                            <Text slot="description" style={{ marginLeft: 8 }}>(Trigger when &gt; 5 errors occur in 5 mins)</Text>
                        </Flex>

                        <Flex direction="row" gap="size-150" alignItems="center">
                            <input
                                type="checkbox"
                                checked={systemHealthAlertEnabled}
                                onChange={(e) => setSystemHealthAlertEnabled(e.target.checked)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                            <Label>Enable System Health Alerts</Label>
                            <Text slot="description" style={{ marginLeft: 8 }}>(Trigger on warn/critical system-health transitions)</Text>
                        </Flex>
                    </Flex>
                </View>
            </Card>


            <View marginTop="size-200">
                <Button variant="primary" onPress={handleSave} isDisabled={isSmtpPending || isAlertPending || isPiiPending}>
                    Save Settings
                </Button>
            </View>
        </Flex>
    );
};

